import { Bell, Moon, Sun, Volume2, VolumeX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/ui/Button";
import { cn } from "@/shared/lib/cn";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";
import { useThemeStore, useResolvedTheme } from "@/shared/hooks/useTheme";
import { useUnresolvedAlerts } from "@/shared/api/hooks/alerts";
import { LANG_LABEL } from "@/shared/i18n/alertPhrases";
import { LanguageMenu } from "./LanguageMenu";

/**
 * UtilityCluster — 언어 · 테마 · 음성 경보 · 미해제 경보 네 버튼.
 *
 * 2026-09-15 까지 상단바(TopBar)에만 있었다. 상단바는 이 넷 말고는 빈 띠라 관제
 * 화면이 60px 를 그냥 잃고 있었다(사용자: "이거 없으면 화면 더 크게 배치될 거 같은데").
 * 데스크톱에서는 상단바를 걷고 이 묶음을 **통합 관제 헤더의 [전체화면] 왼쪽**과
 * 사이드바 아래에 둔다. 폰은 상단바가 그대로 이 묶음을 품는다.
 *
 * 종 버튼은 예전에 **아무 동작이 없는 빈 버튼**이었다(onClick 없음). 이제 미해제
 * 경보 수를 배지로 달고 누르면 경보 센터로 간다 — 「종이 뭐 하는 거냐」는 물음에
 * 답이 있어야 한다.
 */
export function UtilityCluster({ compact = false, className }: { compact?: boolean; className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resolved = useResolvedTheme();
  const toggle = useThemeStore((s) => s.toggle);
  const ThemeIcon = resolved === "dark" ? Moon : Sun;

  const { enabled: ttsEnabled, setEnabled: setTts, supported: ttsSupported, lastFailure: ttsFailure } = useAlertTTS();
  const TtsIcon = ttsEnabled ? Volume2 : VolumeX;
  const ttsFailed = ttsEnabled && !!ttsFailure;
  const ttsTitle = ttsFailure && ttsFailed
    ? t("widgets.topBar.voiceAlertFailed", { lang: LANG_LABEL[ttsFailure.lang], reason: ttsFailure.reason })
    : ttsEnabled ? t("widgets.topBar.voiceAlertEnabled") : t("widgets.topBar.voiceAlertDisabled");

  const unresolved = useUnresolvedAlerts();
  const openCount = unresolved.alerts.length;
  const worst = unresolved.alerts.reduce((m, a) => Math.max(m, Number(a.level ?? 0)), 0);
  const bellTone = worst >= 3 ? "var(--danger)" : worst === 2 ? "var(--warning)" : worst === 1 ? "var(--caution)" : "var(--accent)";

  return (
    <div className={cn("flex items-center", compact ? "gap-0" : "gap-1", className)}>
      <LanguageMenu />

      <Button
        intent="ghost"
        size="icon"
        onClick={toggle}
        aria-label={resolved === "dark" ? t("widgets.topBar.switchToLight") : t("widgets.topBar.switchToDark")}
        title={resolved === "dark" ? t("widgets.topBar.toLight") : t("widgets.topBar.toDark")}
      >
        <ThemeIcon size={17} strokeWidth={1.75} />
      </Button>

      {ttsSupported && (
        <Button
          intent="ghost"
          size="icon"
          onClick={() => setTts(!ttsEnabled)}
          aria-pressed={ttsEnabled}
          aria-label={ttsEnabled ? t("widgets.topBar.voiceAlertOff") : t("widgets.topBar.voiceAlertOn")}
          title={ttsTitle}
          data-tts-failed={ttsFailed ? "1" : undefined}
          className={ttsFailed ? "text-[var(--danger)]" : ttsEnabled ? "text-[var(--accent)]" : undefined}
        >
          <TtsIcon size={17} strokeWidth={1.75} />
        </Button>
      )}

      <Button
        intent="ghost"
        size="icon"
        onClick={() => navigate("/alerts")}
        aria-label={openCount > 0 ? t("widgets.topBar.alertsOpen", { count: openCount }) : t("widgets.topBar.alertsNone")}
        title={openCount > 0 ? t("widgets.topBar.alertsOpen", { count: openCount }) : t("widgets.topBar.alertsNone")}
        className="relative"
      >
        <Bell size={17} strokeWidth={1.75} />
        {openCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-4 text-white text-center tabular-nums"
            style={{ background: bellTone }}
          >
            {openCount > 99 ? "99+" : openCount}
          </span>
        )}
      </Button>
    </div>
  );
}
